import { z } from 'zod';
import { contactEmailKey, isValidContactEmail } from './facilitiesContacts.js';
import { isValidTimeZone } from './localTime.js';
/**
 * Settings, Alerts (Warren, 29 Oct 2026, item 5: "Admin, Settings, Alerts: per-type on/off, thresholds, digest time,
 * EVI ops recipients, a 7-day preview"). One value for the whole network, kept on the EVI tenant's settings row
 * 'alert_settings' (apps/api lib/offlineAlerts/settings.ts), edited by Super Admin and audited with before and after.
 * Pure: the Api reads and validates it, the Admin page shows and edits it, with the same defaults and bounds.
 *
 * Every number, time and list in the rulings is a value here:
 *   types        per-type on/off. Infra stays the ceiling: a type the environment switches off (ALERT_EMAIL_TYPES,
 *                EMAIL_DELIVERY_POLICY) stays off whatever these say; these can only switch a type off.
 *   thresholds   when 'standard' (the default choice) and EVI ops hear live: a charger down longer than
 *                down_after_hours (2), a whole site (two or more active chargers) down longer than site_down_after_min
 *                (30); a network event (more than network_event_sites sites at once) only once it has lasted
 *                network_event_min (30, the site-down threshold) and otherwise in the morning email; the network
 *                checklist after unstable_after_days (3) days unstable, at most every unstable_every_days (7).
 *   digest       the 8 am local site digest (site_time in the site's zone; a site with no zone uses its tenant's,
 *                then fallback_time_zone), EVI ops' morning email (ops_time in ops_time_zone, America/Toronto), and
 *                skip_empty: no digest on a day with nothing to report (ruling C).
 *   ops          EVI ops recipients (only @evinitiative.com; empty falls back to ALERT_OPS_EMAIL) and how they hear
 *                (Standard by default, like the contacts).
 *   staging_allowlist  item 7: outside production no alert email goes to an address not on this list (empty by
 *                default, so nothing goes).
 */
export const ALERT_TYPE_KEYS = [
    'down', 'site_down', 'network_event', 'back_online', 'refused_starts_live', 'unstable_note', 'site_digest', 'ops_morning',
];
export const ALERT_TYPE_LABELS = {
    down: 'Charger down',
    site_down: 'Whole site down',
    network_event: 'Network event (many sites at once), to EVI ops',
    back_online: 'Back online (only after a down email)',
    refused_starts_live: 'Refused starts by email right away, for Standard contacts and EVI ops',
    unstable_note: 'Weekly network checklist for unstable chargers, to buildings',
    site_digest: 'Morning summary to each site',
    ops_morning: 'Morning email to EVI ops',
};
export const ALERT_TYPE_HELP = {
    down: 'One charger offline longer than the down threshold. Contacts on Right away hear at once.',
    site_down: 'Every active charger at a site (two or more) offline longer than the site threshold.',
    network_event: 'Buildings hear nothing about it; EVI ops hears once it has lasted the network event threshold, else in the morning email.',
    back_online: 'Only to whoever got the down email for that outage.',
    refused_starts_live: 'Off: Standard contacts see refused starts in the morning summary and EVI ops in its morning email. Right away contacts keep theirs.',
    unstable_note: 'After the unstable days, at most once per the repeat days. Never to EVI ops.',
    site_digest: 'At the digest time in the site\'s own zone, to Standard and daily summary contacts.',
    ops_morning: 'Network health and the chargers still offline, in one email.',
};
export const ALERT_OPS_DELIVERY_CHOICES = ['weekly_digest', 'standard', 'right_away'];
export const DEFAULT_ALERT_SETTINGS = {
    types: {
        down: true, site_down: true, network_event: true, back_online: true,
        refused_starts_live: false, unstable_note: false, site_digest: true, ops_morning: true,
    },
    thresholds: {
        down_after_hours: 2, site_down_after_min: 30, network_event_sites: 5, network_event_min: 30,
        unstable_after_days: 3, unstable_every_days: 7,
    },
    digest: { site_time: '08:00', ops_time: '08:00', ops_time_zone: 'America/Toronto', fallback_time_zone: 'America/Toronto', skip_empty: true },
    ops: { recipients: [], delivery: 'weekly_digest' },
    staging_allowlist: [],
};
/** [min, max] of each threshold, and whether it takes a fraction (hours in quarters). */
export const ALERT_THRESHOLD_BOUNDS = {
    down_after_hours: { min: 0.25, max: 24, step: 0.25, label: 'Charger down for (hours)' },
    site_down_after_min: { min: 5, max: 240, step: 5, label: 'Whole site down for (minutes)' },
    network_event_sites: { min: 2, max: 100, step: 1, label: 'Network event: more than this many sites at once' },
    network_event_min: { min: 5, max: 240, step: 5, label: 'Network event lasts (minutes) before EVI ops hears' },
    unstable_after_days: { min: 1, max: 14, step: 1, label: 'Unstable for (days) before the checklist' },
    unstable_every_days: { min: 1, max: 30, step: 1, label: 'Checklist at most every (days)' },
};
export const ALERT_OPS_RECIPIENTS_MAX = 10;
export const ALERT_STAGING_ALLOWLIST_MAX = 20;
export const EVI_OPS_DOMAIN = 'evinitiative.com';
/** An EVI ops recipient must be an EV Initiative address (ruling G). */
export function isEviOpsAddress(email) {
    const key = contactEmailKey(email);
    return isValidContactEmail(email.trim()) && key.endsWith(`@${EVI_OPS_DOMAIN}`);
}
const CLOCK = /^([01]\d|2[0-3]):([0-5]\d)$/;
/** Minutes after midnight of an 'HH:MM' time; null when it is not one. */
export function clockMinutes(value) {
    if (typeof value !== 'string')
        return null;
    const m = CLOCK.exec(value);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}
const multipleOf = (step) => (v) => Math.abs(Math.round(v / step) * step - v) < 1e-9;
const threshold = (k) => {
    const b = ALERT_THRESHOLD_BOUNDS[k];
    return z.number({ error: `${b.label}: a number.` }).min(b.min, `${b.label}: at least ${b.min}.`).max(b.max, `${b.label}: at most ${b.max}.`)
        .refine(multipleOf(b.step), `${b.label}: in steps of ${b.step}.`);
};
const clock = (label) => z.string().refine((v) => clockMinutes(v) !== null, `${label}: a time like 08:00.`);
const zone = (label) => z.string().max(64).refine(isValidTimeZone, `${label}: a time zone like America/Toronto.`);
const dedupe = (list) => {
    const seen = new Set();
    const out = [];
    for (const raw of list) {
        const e = raw.trim();
        const k = contactEmailKey(e);
        if (seen.has(k))
            continue;
        seen.add(k);
        out.push(e);
    }
    return out;
};
export const AlertSettingsSchema = z.object({
    types: z.object(Object.fromEntries(ALERT_TYPE_KEYS.map((k) => [k, z.boolean()]))).strict(),
    thresholds: z.object(Object.fromEntries(Object.keys(ALERT_THRESHOLD_BOUNDS).map((k) => [k, threshold(k)]))).strict(),
    digest: z.object({
        site_time: clock('Site digest time'),
        ops_time: clock('EVI ops morning time'),
        ops_time_zone: zone('EVI ops time zone'),
        fallback_time_zone: zone('Fallback time zone'),
        skip_empty: z.boolean(),
    }).strict(),
    ops: z.object({
        recipients: z.array(z.string().max(254)).max(ALERT_OPS_RECIPIENTS_MAX, `At most ${ALERT_OPS_RECIPIENTS_MAX} EVI ops recipients.`)
            .refine((l) => l.every(isEviOpsAddress), `EVI ops recipients must be @${EVI_OPS_DOMAIN} addresses.`).transform(dedupe),
        delivery: z.enum(ALERT_OPS_DELIVERY_CHOICES),
    }).strict(),
    staging_allowlist: z.array(z.string().max(254)).max(ALERT_STAGING_ALLOWLIST_MAX, `At most ${ALERT_STAGING_ALLOWLIST_MAX} staging addresses.`)
        .refine((l) => l.every((e) => isValidContactEmail(e.trim())), 'Each staging address must be an email address.').transform(dedupe),
}).strict();
/**
 * A stored value read with the defaults filled in: a missing row, a missing part or a value that no longer validates
 * reads as the default for that part, so a bad row can never stop the alerts.
 */
export function alertSettingsOf(raw) {
    const v = (raw && typeof raw === 'object' ? raw : {});
    const part = (key) => {
        const shape = AlertSettingsSchema.shape[key];
        const merged = mergeDefaults(DEFAULT_ALERT_SETTINGS[key], v[key]);
        const parsed = shape.safeParse(merged);
        return (parsed.success ? parsed.data : DEFAULT_ALERT_SETTINGS[key]);
    };
    return { types: part('types'), thresholds: part('thresholds'), digest: part('digest'), ops: part('ops'), staging_allowlist: part('staging_allowlist') };
}
function mergeDefaults(def, value) {
    if (Array.isArray(def))
        return Array.isArray(value) ? value : def;
    if (def && typeof def === 'object') {
        const o = (value && typeof value === 'object' && !Array.isArray(value) ? value : {});
        return Object.fromEntries(Object.entries(def).map(([k, d]) => [k, k in o ? mergeDefaults(d, o[k]) : d]));
    }
    return value === undefined ? def : value;
}
/** The milliseconds a 'standard' recipient waits: a charger, and a whole site. */
export function standardWaits(s) {
    return { chargerMs: Math.round(s.thresholds.down_after_hours * 60) * 60_000, siteMs: s.thresholds.site_down_after_min * 60_000 };
}
/** "Standard: a summary at 8:00 AM, plus an email when a charger is down over 2 hours or the whole site over 30 minutes." */
export function standardDeliveryLabel(s) {
    const mins = clockMinutes(s.digest.site_time) ?? 480;
    const h = Math.floor(mins / 60), m = mins % 60;
    const at = `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
    const hours = s.thresholds.down_after_hours;
    return `Standard: a summary at ${at}, plus an email when a charger is down over ${hours} ${hours === 1 ? 'hour' : 'hours'} `
        + `or the whole site over ${s.thresholds.site_down_after_min} minutes`;
}
//# sourceMappingURL=alertSettings.js.map